import { useState, useEffect, useCallback, useRef } from 'react'
import { VideoService } from '../services/videos'
import { CategoryService } from '../services/categories'
import { TagService } from '../services/tags'
import { AuthService } from '../services/auth'
import { AttachmentService, type VideoAttachment } from '../services/attachments'
import { ChatPromptService } from '../services/chatPrompts'
import AttachmentManagementSection from './teacher/AttachmentManagementSection'
import VideoThumbnail from './VideoThumbnail'
import { toLocalDateTimeString } from '../utils/datetimeLocal'
import { validateVimeoVideo, autoImportVimeoVideo, getVimeoThumbnailUrl, extractVimeoParams as extractVimeoParamsUtil, fetchVimeoMetadata } from '../utils/vimeo'
import type { Video } from '../services/videos'
import type { Tag } from '../services/tags'

interface VideoFormData {
  title: string
  description: string
  vimeo_id: string
  vimeo_h: string
  youtube_id: string
  category: string
  tags: string[] // Keep as string array for backward compatibility, but these will be tag IDs
  duration: string | null
  thumbnail_url: string
  video_type: 'regular' | 'demo' | 'chat_only'
  published_at: string
}


const VideoManager = () => {
  const [videos, setVideos] = useState<Video[]>([])
  const [categories, setCategories] = useState<Array<{ id: string; name: string; description?: string }>>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [videoTags, setVideoTags] = useState<Record<string, Tag[]>>({}) // Maps video ID to its tags
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingVideo, setEditingVideo] = useState<Video | null>(null)
  const [formData, setFormData] = useState<VideoFormData>({
    title: '',
    description: '',
    vimeo_id: '',
    vimeo_h: '',
    youtube_id: '',
    category: '',
    tags: [],
    duration: null,
    thumbnail_url: '',
    video_type: 'regular',
    published_at: ''
  })
  const [tagInput, setTagInput] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([]) // Tag IDs for form
  const [showTagSelector, setShowTagSelector] = useState(false)
  const [tagSearchQuery, setTagSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState({
    category: 'all',
    search: ''
  })
  const formRef = useRef<HTMLDivElement>(null)
  const [showAutoImport, setShowAutoImport] = useState(false)
  const [autoImportUrl, setAutoImportUrl] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set())
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [showQuickCategoryForm, setShowQuickCategoryForm] = useState(false)
  const [quickCategoryName, setQuickCategoryName] = useState('')
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [currentVideoAttachments, setCurrentVideoAttachments] = useState<VideoAttachment[]>([])
  const [videoAttachments, setVideoAttachments] = useState<Record<string, VideoAttachment[]>>({})

  const loadCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true)
      const categoriesData = await CategoryService.getCategoriesForDropdown()
      setCategories(categoriesData)
    } catch (error) {
      console.error('Failed to load categories:', error)
      setError('カテゴリーの読み込みに失敗しました')
    } finally {
      setCategoriesLoading(false)
    }
  }, [])

  const loadTags = useCallback(async () => {
    try {
      const tagsData = await TagService.getTags()
      setTags(tagsData)
    } catch (error) {
      console.error('Failed to load tags:', error)
      setError('タグの読み込みに失敗しました')
    }
  }, [])

  const loadVideos = useCallback(async () => {
    try {
      setIsLoading(true)
      const videosData = await VideoService.getVideos({
        category: filter.category !== 'all' ? filter.category : undefined,
        search: filter.search || undefined
      })
      setVideos(videosData)

      // Load tags and attachments for all videos in batch (optimized: 2 queries instead of N+N)
      const videoIds = videosData.map(v => v.id)

      let videoTagsMap: Record<string, Tag[]> = {}
      let videoAttachmentsMap: Record<string, VideoAttachment[]> = {}

      try {
        videoTagsMap = await TagService.getVideoTagsBatch(videoIds)
      } catch (error) {
        console.error('Failed to load tags batch:', error)
        // Initialize empty arrays for all videos on error
        videoIds.forEach(id => { videoTagsMap[id] = [] })
      }

      try {
        videoAttachmentsMap = await AttachmentService.getVideoAttachmentsBatch(videoIds)
      } catch (error) {
        console.error('Failed to load attachments batch:', error)
        // Initialize empty arrays for all videos on error
        videoIds.forEach(id => { videoAttachmentsMap[id] = [] })
      }

      setVideoTags(videoTagsMap)
      setVideoAttachments(videoAttachmentsMap)
    } catch (error) {
      console.error('Failed to load videos:', error)
      setError('動画の読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [filter])

  useEffect(() => {
    loadCategories()
    loadTags()
    loadVideos()
  }, [loadCategories, loadTags, loadVideos])

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      vimeo_id: '',
      vimeo_h: '',
      youtube_id: '',
      category: '',
      tags: [],
      duration: null,
      thumbnail_url: '',
      video_type: 'regular',
      published_at: ''
    })
    setTagInput('')
    setSelectedTags([])
    setShowTagSelector(false)
    setTagSearchQuery('')
    setEditingVideo(null)
    setError('')
    setThumbnailFile(null)
    setThumbnailPreview(null)
    setIsUploading(false)
    setCurrentVideoAttachments([])
  }

  const handleAddVideo = () => {
    resetForm()
    setShowAddForm(true)
  }

  const handleEditVideo = async (video: Video) => {
    const currentVideoTags = videoTags[video.id] || []

    setFormData({
      title: video.title,
      description: video.description || '',
      vimeo_id: video.vimeo_id || '',
      vimeo_h: video.vimeo_h || '',
      youtube_id: video.youtube_id || '',
      category: video.category,
      tags: currentVideoTags.map(tag => tag.id), // Set tag IDs
      duration: video.duration,
      thumbnail_url: video.thumbnail_url || '',
      video_type: video.video_type || 'regular',
      published_at: video.published_at ? toLocalDateTimeString(video.published_at) : ''
    })
    setSelectedTags(currentVideoTags.map(tag => tag.id))
    setEditingVideo(video)

    // Load attachments for editing video
    try {
      const attachments = await AttachmentService.getVideoAttachments(video.id)
      setCurrentVideoAttachments(attachments)
    } catch (error) {
      console.error('Failed to load attachments:', error)
      setCurrentVideoAttachments([])
    }

    setShowAddForm(true)

    // Scroll to the form
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)

    // Set thumbnail preview if thumbnail URL exists
    if (video.thumbnail_url) {
      setThumbnailPreview(video.thumbnail_url)
    }
  }

  // Every video should have its own chat prompt row so it gets its own chat and
  // conversation thread. The system prompt is left empty here (admins configure it
  // separately in みらいワーク管理); the chat falls back to the generic system prompt
  // at request time.
  const ensureChatPrompt = async (videoId: string, title: string): Promise<void> => {
    try {
      const existing = await ChatPromptService.getPromptByVideoId(videoId).catch(() => null)
      if (existing) return
      await ChatPromptService.createPrompt({
        video_id: videoId,
        title,
        system_prompt: '',
        initial_message: null,
        is_active: true,
      })
    } catch (err) {
      console.warn('Could not ensure chat prompt for video:', err)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      // Validate category selection
      if (!formData.category || formData.category.trim() === '') {
        setError('カテゴリーを選択してください')
        return
      }

      // Validate video type requirements
      if (formData.video_type === 'demo' || formData.video_type === 'chat_only') {
        if (!thumbnailFile && !formData.thumbnail_url) {
          setError('デモ動画とチャットのみの動画にはサムネイル画像が必要です')
          return
        }
      } else {
        // Regular video validation
        if (!formData.vimeo_id || formData.vimeo_id.trim() === '') {
          setError('通常の動画にはVimeo IDが必要です')
          return
        }
      }

      // Get the category object to convert slug to category_id
      const selectedCategory = await CategoryService.getCategoryBySlug(formData.category)
      if (!selectedCategory) {
        setError('選択されたカテゴリーが見つかりません。カテゴリーを再選択してください。')
        await loadCategories() // Refresh categories in case they were updated
        return
      }

      let videoId: string
      setIsUploading(true)
      
      if (editingVideo) {
        // Update existing video
        const { tags, category, ...videoUpdateData } = formData
        void tags; void category;

        if (formData.video_type === 'demo' || formData.video_type === 'chat_only') {
          // Handle demo/chat_only video update
          const newVideo = await VideoService.updateDemoVideo(editingVideo.id, {
            ...videoUpdateData,
            category_id: selectedCategory.id,
            category: formData.category,
            published_at: videoUpdateData.published_at ? new Date(videoUpdateData.published_at).toISOString() : null,
            duration: null, // Duration not required for demo/chat_only videos
          }, thumbnailFile || undefined)
          videoId = newVideo.id
        } else {
          // Handle regular video update
          await VideoService.updateVideo(editingVideo.id, {
            ...videoUpdateData,
            category_id: selectedCategory.id,
            category: formData.category,
            published_at: videoUpdateData.published_at ? new Date(videoUpdateData.published_at).toISOString() : null,
          }, thumbnailFile || undefined)
          videoId = editingVideo.id
        }

        await AuthService.logAdminAction('update_video', 'video', editingVideo.id, {
          title: formData.title,
          vimeo_id: formData.vimeo_id,
          category: formData.category,
          video_type: formData.video_type
        })
      } else {
        // Create new video
        const { tags, category, video_type, ...videoCreateData } = formData
        void tags; void category;

        if (video_type === 'demo' || video_type === 'chat_only') {
          // Handle demo/chat_only video creation
          const newVideo = await VideoService.createDemoVideo({
            ...videoCreateData,
            category_id: selectedCategory.id,
            category: formData.category,
            view_count: 0,
            video_type: video_type, // Explicitly pass the narrowed type
            duration: null, // Duration not required for demo/chat_only videos
          }, thumbnailFile || undefined)
          videoId = newVideo.id
        } else {
          // Handle regular video creation
          const newVideo = await VideoService.createVideo({
            ...videoCreateData,
            category_id: selectedCategory.id,
            category: formData.category,
            published_at: videoCreateData.published_at ? new Date(videoCreateData.published_at).toISOString() : null,
            view_count: 0,
            video_type: video_type, // Explicitly pass 'regular'
          }, thumbnailFile || undefined)
          videoId = newVideo.id
        }

        await AuthService.logAdminAction('create_video', 'video', videoId, {
          title: formData.title,
          category: formData.category,
          video_type: formData.video_type
        })
      }

      // Make sure every video has its own chat prompt row (creates one if missing).
      await ensureChatPrompt(videoId, formData.title)

      // Update video tags separately
      if (selectedTags.length > 0 || editingVideo) {
        await TagService.updateVideoTags(videoId, selectedTags)
      }

      setShowAddForm(false)
      resetForm()
      await loadVideos()
    } catch (err: unknown) {
      console.error('Failed to save video:', err)
      const errorMessage = err instanceof Error ? err.message : String(err)
      setError(errorMessage || '動画の保存に失敗しました')
    } finally {
      setIsLoading(false)
      setIsUploading(false)
    }
  }

  const handleDeleteVideo = async (video: Video) => {
    if (confirm(`「${video.title}」を削除してもよろしいですか？`)) {
      try {
        setIsLoading(true)
        await VideoService.deleteVideo(video.id)
        
        await AuthService.logAdminAction('delete_video', 'video', video.id, {
          title: video.title
        })
        
        await loadVideos()
      } catch (error: unknown) {
        console.error('Failed to delete video:', error)
        const errorMessage = error instanceof Error ? error.message : String(error)
        setError(errorMessage || '動画の削除に失敗しました')
      } finally {
        setIsLoading(false)
      }
    }
  }

  // Tag management functions
  const addSelectedTag = (tagId: string) => {
    if (!selectedTags.includes(tagId)) {
      const newSelectedTags = [...selectedTags, tagId]
      setSelectedTags(newSelectedTags)
      setFormData({
        ...formData,
        tags: newSelectedTags
      })
    }
    setShowTagSelector(false)
    setTagSearchQuery('')
  }

  const removeSelectedTag = (tagId: string) => {
    const newSelectedTags = selectedTags.filter(id => id !== tagId)
    setSelectedTags(newSelectedTags)
    setFormData({
      ...formData,
      tags: newSelectedTags
    })
  }

  const createNewTag = async () => {
    if (!tagInput.trim()) return

    try {
      const slug = await TagService.generateUniqueSlug(tagInput.trim())
      // Use the first available category as default
      const defaultCategoryId = categories.find(cat => cat.id !== 'all')?.id
      if (!defaultCategoryId) {
        setError('カテゴリーが見つかりません')
        return
      }
      
      const newTag = await TagService.createTag({
        name: tagInput.trim(),
        slug,
        description: null,
        category_id: defaultCategoryId,
        is_active: true
      })
      
      // Refresh tags list
      await loadTags()
      
      // Add the new tag to selected tags
      addSelectedTag(newTag.id)
      setTagInput('')
    } catch (error) {
      console.error('Failed to create tag:', error)
      setError('タグの作成に失敗しました')
    }
  }

  // Filter tags for selector
  const getFilteredTags = () => {
    return tags.filter(tag => 
      tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase()) &&
      !selectedTags.includes(tag.id)
    )
  }

  const extractVimeoParams = (url: string) => {
    return extractVimeoParamsUtil(url)
  }

  const handleVimeoUrlChange = (value: string) => {
    const { videoId, h } = extractVimeoParams(value)
    setFormData({
      ...formData,
      vimeo_id: videoId || value,
      vimeo_h: h || ''
    })

    // Auto-generate thumbnail URL if not provided
    if (videoId && !formData.thumbnail_url) {
      setFormData(prev => ({
        ...prev,
        thumbnail_url: getVimeoThumbnailUrl(videoId)
      }))
    }
  }

  const handleUpdateThumbnailFromVimeo = async () => {
    if (!formData.vimeo_id) return

    try {
      // Try to fetch thumbnail from Vimeo API
      const metadata = await fetchVimeoMetadata(formData.vimeo_id, formData.vimeo_h || null)
      const thumbnailUrl = metadata.thumbnail

      setFormData(prev => ({
        ...prev,
        thumbnail_url: thumbnailUrl
      }))
      setThumbnailPreview(thumbnailUrl)
    } catch (error) {
      console.error('Failed to fetch thumbnail from Vimeo API, using fallback:', error)

      // Fallback to vumbnail.com if API fails
      const thumbnailUrl = getVimeoThumbnailUrl(formData.vimeo_id)
      setFormData(prev => ({
        ...prev,
        thumbnail_url: thumbnailUrl
      }))
      setThumbnailPreview(thumbnailUrl)
    }
  }

  const handleAutoImport = async () => {
    const { videoId, h: vimeo_h } = extractVimeoParams(autoImportUrl)
    if (!videoId) {
      setError('有効なVimeo URLまたはIDを入力してください')
      return
    }

    try {
      setIsValidating(true)
      setError('')

      // Validate video first
      const validation = await validateVimeoVideo(videoId, vimeo_h)
      if (!validation.isValid) {
        setError(validation.error || '動画にアクセスできません')
        return
      }

      // Auto-import video data (use first available category or 'beginner' as fallback)
      const defaultCategory = categories.find(cat => cat.id !== 'all')?.id || 'beginner'
      const importedData = await autoImportVimeoVideo(videoId, defaultCategory, vimeo_h)
      
      // Convert imported tag names to tag IDs (or create new tags)
      const tagIds: string[] = []
      for (const tagName of importedData.tags) {
        // Find existing tag by name
        let existingTag = tags.find(tag => tag.name.toLowerCase() === tagName.toLowerCase())
        
        if (!existingTag) {
          // Create new tag if it doesn't exist
          try {
            const slug = await TagService.generateUniqueSlug(tagName)
            const defaultCategoryId = categories.find(cat => cat.id !== 'all')?.id
            if (!defaultCategoryId) {
              console.warn(`No default category found for tag: ${tagName}`)
              continue
            }
            
            existingTag = await TagService.createTag({
              name: tagName,
              slug,
              description: null,
              category_id: defaultCategoryId,
              is_active: true
            })
            // Refresh tags list
            await loadTags()
          } catch (error) {
            console.error(`Failed to create tag "${tagName}":`, error)
            continue
          }
        }
        
        if (existingTag) {
          tagIds.push(existingTag.id)
        }
      }
      
      // Fill form with imported data, including extracted vimeo_h
      setFormData({
        title: importedData.title,
        description: importedData.description,
        vimeo_id: importedData.vimeo_id,
        vimeo_h: vimeo_h || importedData.vimeo_h || '',
        youtube_id: '',
        category: importedData.category,
        tags: tagIds,
        duration: importedData.duration,
        thumbnail_url: importedData.thumbnail_url,
        video_type: 'regular',
        published_at: ''
      })
      
      setSelectedTags(tagIds)

      setShowAutoImport(false)
      setShowAddForm(true)
      setAutoImportUrl('')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || '動画のインポートに失敗しました')
    } finally {
      setIsValidating(false)
    }
  }

  const toggleVideoSelection = (videoId: string) => {
    const newSelected = new Set(selectedVideos)
    if (newSelected.has(videoId)) {
      newSelected.delete(videoId)
    } else {
      newSelected.add(videoId)
    }
    setSelectedVideos(newSelected)
  }

  const selectAllVideos = () => {
    if (selectedVideos.size === videos.length) {
      setSelectedVideos(new Set())
    } else {
      setSelectedVideos(new Set(videos.map(v => v.id)))
    }
  }

  const handleBulkDelete = async () => {
    if (selectedVideos.size === 0) return

    const confirmMessage = `選択された${selectedVideos.size}件の動画を削除してもよろしいですか？`
    if (!confirm(confirmMessage)) return

    try {
      setIsLoading(true)
      const deletePromises = Array.from(selectedVideos).map(async (videoId) => {
        await VideoService.deleteVideo(videoId)
        const video = videos.find(v => v.id === videoId)
        if (video) {
          await AuthService.logAdminAction('bulk_delete_video', 'video', videoId, {
            title: video.title
          })
        }
      })

      await Promise.all(deletePromises)
      setSelectedVideos(new Set())
      await loadVideos()
    } catch (error: unknown) {
      console.error('Failed to bulk delete videos:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || '一括削除に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleQuickCreateCategory = async () => {
    if (!quickCategoryName.trim()) {
      setError('カテゴリー名を入力してください')
      return
    }

    try {
      setIsLoading(true)
      const slug = quickCategoryName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()

      const newCategory = await CategoryService.createCategory({
        name: quickCategoryName,
        slug: slug || 'category-' + Date.now(),
        description: `動画管理から作成されたカテゴリー: ${quickCategoryName}`,
        is_active: true
      })

      await AuthService.logAdminAction('create_category_from_video', 'category', newCategory.id, {
        name: quickCategoryName,
        created_from: 'video_manager'
      })

      // Reload categories and set the new category as selected
      await loadCategories()
      setFormData({ ...formData, category: newCategory.slug })
      setShowQuickCategoryForm(false)
      setQuickCategoryName('')
      setError('')

      alert(`カテゴリー「${quickCategoryName}」を作成しました。`)
    } catch (err: unknown) {
      console.error('Failed to create category:', err)
      const errorMessage = err instanceof Error ? err.message : String(err)
      setError(errorMessage || 'カテゴリーの作成に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle thumbnail file selection
  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('画像ファイルを選択してください')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('ファイルサイズは5MB以下にしてください')
        return
      }

      setThumbnailFile(file)
      setError('')

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setThumbnailPreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Remove thumbnail
  const handleRemoveThumbnail = () => {
    setThumbnailFile(null)
    setThumbnailPreview(null)
    // Reset file input
    const fileInput = document.getElementById('thumbnail-upload') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const handleBulkCategoryUpdate = async (newCategory: string) => {
    if (selectedVideos.size === 0) return

    // Get the category object to convert slug to category_id
    const selectedCategory = await CategoryService.getCategoryBySlug(newCategory)
    if (!selectedCategory) {
      setError('選択されたカテゴリーが見つかりません。カテゴリー管理で確認してください。')
      await loadCategories() // Refresh categories
      return
    }

    const categoryName = selectedCategory.name
    
    // Confirmation dialog
    if (!confirm(`選択された${selectedVideos.size}件の動画のカテゴリーを「${categoryName}」に変更してもよろしいですか？`)) {
      return
    }

    try {
      setIsLoading(true)
      const updatePromises = Array.from(selectedVideos).map(async (videoId) => {
        await VideoService.updateVideo(videoId, { 
          category_id: selectedCategory.id,
          category: newCategory // Keep legacy category field for backward compatibility
        })
        const video = videos.find(v => v.id === videoId)
        if (video) {
          await AuthService.logAdminAction('bulk_update_category', 'video', videoId, {
            title: video.title,
            old_category: video.category,
            new_category: newCategory
          })
        }
      })

      await Promise.all(updatePromises)
      setSelectedVideos(new Set())
      setShowBulkActions(false)
      await loadVideos()
      
      // Success message
      setError('') // Clear any previous errors
      alert(`${selectedVideos.size}件の動画のカテゴリーを「${categoryName}」に変更しました。`)
    } catch (error: unknown) {
      console.error('Failed to bulk update categories:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setError(errorMessage || 'カテゴリーの一括更新に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">動画管理</h2>
          <p className="text-sm text-gray-600">Vimeoに保存された動画を管理します</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowAutoImport(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Vimeoから自動インポート
          </button>
          <button
            onClick={handleAddVideo}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            手動で動画を追加
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">カテゴリー</label>
            <div className="relative">
              <select
                value={filter.category}
                onChange={(e) => setFilter({ ...filter, category: e.target.value })}
                disabled={categoriesLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:opacity-50"
              >
                {categoriesLoading ? (
                  <option value="">読み込み中...</option>
                ) : (
                  categories.map(cat => (
                    <option key={cat.id} value={cat.id} title={cat.description}>
                      {cat.name}
                    </option>
                  ))
                )}
              </select>
              {categoriesLoading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">検索</label>
            <input
              type="text"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              placeholder="タイトルまたは説明文で検索"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={loadVideos}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              更新
            </button>
          </div>
        </div>
      </div>

      {/* Auto-Import Modal */}
      {showAutoImport && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-base font-medium text-gray-900 mb-4">
            Vimeoから動画を自動インポート
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Vimeo URLまたは動画IDを入力すると、タイトル、説明、サムネイルなどの情報を自動で取得します。
            非公開動画の場合は、hパラメータも自動で抽出されます。
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vimeo URL または 動画ID
              </label>
              <input
                type="text"
                value={autoImportUrl}
                onChange={(e) => setAutoImportUrl(e.target.value)}
                placeholder="https://vimeo.com/123456789?h=abcdef123 または 123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            
            <div className="bg-blue-50 p-3 rounded-md">
              <h4 className="text-sm font-medium text-blue-900 mb-1">使用方法:</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Vimeo動画のURLをコピーして貼り付け</li>
                <li>• または動画IDのみを入力</li>
                <li>• 非公開動画の場合、hパラメータも自動で抽出されます</li>
                <li>• 自動で動画情報を取得してフォームに入力します</li>
                <li>• カテゴリーやタグは後で編集できます</li>
              </ul>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAutoImport(false)
                  setAutoImportUrl('')
                  setError('')
                }}
                disabled={isValidating}
                className="bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleAutoImport}
                disabled={!autoImportUrl.trim() || isValidating}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {isValidating ? 'インポート中...' : 'インポート'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div ref={formRef} className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-base font-medium text-gray-900 mb-4">
            {editingVideo ? '動画を編集' : '新しい動画を追加'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Video Type Selector */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  動画タイプ *
                </label>
                <select
                  required
                  value={formData.video_type}
                  onChange={(e) => setFormData({ ...formData, video_type: e.target.value as 'regular' | 'demo' | 'chat_only' })}
                  className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="regular">通常動画 - Vimeo動画が必要です</option>
                  <option value="demo">デモ動画 - サムネイル画像のみ表示</option>
                  <option value="chat_only">チャットのみ - 動画なし、チャット画面のみ</option>
                </select>
                <p className="text-xs text-blue-700 mt-2">
                  {formData.video_type === 'regular' && 'Vimeo動画を埋め込みます'}
                  {formData.video_type === 'demo' && 'サムネイル画像のみでVimeo動画は不要です'}
                  {formData.video_type === 'chat_only' && 'サムネイルは表示されず、チャット画面が全幅で表示されます'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  タイトル *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  カテゴリー *
                </label>
                <div className="relative">
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    disabled={categoriesLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:opacity-50"
                  >
                    <option value="">
                      {categoriesLoading ? 'カテゴリーを読み込み中...' : 'カテゴリーを選択'}
                    </option>
                    {categories.filter(cat => cat.id !== 'all').map(cat => (
                      <option key={cat.id} value={cat.id} title={cat.description}>
                        {cat.name}
                        {cat.description && ` - ${cat.description.substring(0, 30)}${cat.description.length > 30 ? '...' : ''}`}
                      </option>
                    ))}
                  </select>
                  {categoriesLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  {categories.length === 1 && (
                    <p className="text-xs text-amber-600">
                      ⚠️ カテゴリーがありません
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowQuickCategoryForm(true)}
                    className="text-xs text-blue-600 hover:text-blue-500 font-medium"
                  >
                    + 新しいカテゴリーを作成
                  </button>
                </div>

                {/* Quick Category Creation Form */}
                {showQuickCategoryForm && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={quickCategoryName}
                        onChange={(e) => setQuickCategoryName(e.target.value)}
                        placeholder="カテゴリー名を入力"
                        className="flex-1 px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onKeyDown={(e) => e.key === 'Enter' && handleQuickCreateCategory()}
                      />
                      <button
                        type="button"
                        onClick={handleQuickCreateCategory}
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-2 py-1 rounded text-xs font-medium"
                      >
                        作成
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowQuickCategoryForm(false)
                          setQuickCategoryName('')
                        }}
                        className="text-gray-500 hover:text-gray-600 text-xs"
                      >
                        キャンセル
                      </button>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      作成後、自動でこのカテゴリーが選択されます
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                説明文
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Conditional Vimeo fields for regular videos */}
            {formData.video_type === 'regular' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vimeo URL または ID *
                  </label>
                  <input
                    type="text"
                    required={formData.video_type === 'regular'}
                    value={formData.vimeo_id}
                    onChange={(e) => handleVimeoUrlChange(e.target.value)}
                    placeholder="https://vimeo.com/123456789 または 123456789"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vimeo Hパラメータ (非公開動画用)
                  </label>
                  <input
                    type="text"
                    value={formData.vimeo_h}
                    onChange={(e) => setFormData({ ...formData, vimeo_h: e.target.value })}
                    placeholder="例: 5439ef4d27"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    非公開動画の場合にのみ必要です
                  </p>
                </div>
              </div>
            )}

            {/* Thumbnail upload for all video types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                サムネイル画像 {(formData.video_type === 'demo' || formData.video_type === 'chat_only') && '*'}
              </label>
                <div className="space-y-3">
                  <input
                    id="thumbnail-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500">
                    JPG、PNG、GIF対応（最大5MB）
                  </p>

                  {/* Thumbnail URL field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      サムネイル URL
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={formData.thumbnail_url}
                        onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                        placeholder="自動生成されます"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {formData.vimeo_id && (
                        <button
                          type="button"
                          onClick={handleUpdateThumbnailFromVimeo}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors duration-200 whitespace-nowrap"
                        >
                          Vimeoから取得
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Thumbnail preview */}
                  {thumbnailPreview && (
                    <div className="relative inline-block">
                      <img
                        src={thumbnailPreview}
                        alt="サムネイルプレビュー"
                        className="w-40 h-24 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveThumbnail}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              {formData.video_type === 'regular' && (
                <p className="text-xs text-gray-500 mt-1">
                  通常動画の場合、画像をアップロードしない場合はVimeoから自動取得されます
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  YouTube ID (オプション)
                </label>
                <input
                  type="text"
                  value={formData.youtube_id}
                  onChange={(e) => setFormData({ ...formData, youtube_id: e.target.value })}
                  placeholder="dQw4w9WgXcQ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  動画の長さ {formData.video_type === 'regular' && '*'}
                </label>
                <input
                  type="text"
                  required={formData.video_type === 'regular'}
                  value={formData.duration || ''}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value || null })}
                  placeholder="例: 12:30, 5:45"
                  disabled={formData.video_type === 'demo' || formData.video_type === 'chat_only'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                />
                {(formData.video_type === 'demo' || formData.video_type === 'chat_only') && (
                  <p className="text-xs text-gray-500 mt-1">
                    デモ動画とチャットのみの動画には不要です
                  </p>
                )}
              </div>
              <div></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  公開日時
                </label>
                <input
                  type="datetime-local"
                  value={formData.published_at}
                  onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  空欄の場合は未公開として扱われます
                </p>
              </div>
              <div></div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                タグ
              </label>
              
              {/* Selected Tags */}
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedTags.map((tagId) => {
                  const tag = tags.find(t => t.id === tagId)
                  return tag ? (
                    <span
                      key={tagId}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag.name}
                      <button
                        type="button"
                        onClick={() => removeSelectedTag(tagId)}
                        className="ml-1 text-blue-600 hover:text-blue-500"
                      >
                        ×
                      </button>
                    </span>
                  ) : null
                })}
              </div>

              {/* Tag Selection */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setShowTagSelector(!showTagSelector)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm"
                >
                  {showTagSelector ? 'タグ選択を閉じる' : 'タグを選択'}
                </button>

                {showTagSelector && (
                  <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
                    {/* Search existing tags */}
                    <div className="mb-2">
                      <input
                        type="text"
                        value={tagSearchQuery}
                        onChange={(e) => setTagSearchQuery(e.target.value)}
                        placeholder="既存タグを検索..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    {/* Existing tags */}
                    <div className="max-h-32 overflow-y-auto mb-3">
                      {getFilteredTags().length > 0 ? (
                        <div className="space-y-1">
                          {getFilteredTags().map((tag) => (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => addSelectedTag(tag.id)}
                              className="block w-full text-left text-sm px-2 py-1 hover:bg-blue-100 rounded"
                            >
                              {tag.name}
                              {tag.description && (
                                <span className="text-gray-500"> - {tag.description}</span>
                              )}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">該当するタグがありません</p>
                      )}
                    </div>

                    {/* Create new tag */}
                    <div className="border-t pt-2">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), createNewTag())}
                          placeholder="新しいタグ名"
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={createNewTag}
                          disabled={!tagInput.trim()}
                          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-2 py-1 rounded text-sm"
                        >
                          作成
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        新しいタグを作成して追加
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Attachment Management - Only show for existing videos */}
            {editingVideo && (
              <AttachmentManagementSection
                video={editingVideo}
                attachments={currentVideoAttachments}
                onAttachmentsUpdate={async () => {
                  const attachments = await AttachmentService.getVideoAttachments(editingVideo.id)
                  setCurrentVideoAttachments(attachments)
                  loadVideos() // Refresh the video list to update attachment counts
                }}
                onError={setError}
              />
            )}

            {/* Preview */}
            {(formData.vimeo_id && formData.video_type === 'regular') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vimeoプレビュー
                </label>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="aspect-w-16 aspect-h-9 mb-3">
                    <iframe
                      src={`https://player.vimeo.com/video/${formData.vimeo_id}${formData.vimeo_h ? `?h=${formData.vimeo_h}` : ''}`}
                      className="w-full h-64 rounded"
                      style={{ border: 0 }}
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                  <p className="text-sm text-gray-600">
                    Vimeo ID: {formData.vimeo_id}
                    {formData.vimeo_h && <span> (h={formData.vimeo_h})</span>}
                  </p>
                </div>
              </div>
            )}
            
            {/* Demo and chat_only video preview */}
            {(formData.video_type === 'demo' || formData.video_type === 'chat_only') && thumbnailPreview && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  デモ動画プレビュー
                </label>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="relative">
                    <img
                      src={thumbnailPreview}
                      alt="デモ動画サムネイル"
                      className="w-full max-w-md h-64 object-cover rounded"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded">
                      <div className="text-white text-center">
                        <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-2">
                          <div className="w-0 h-0 border-l-8 border-l-white border-t-6 border-b-6 border-t-transparent border-b-transparent ml-1"></div>
                        </div>
                        <p className="text-sm">デモ動画</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    サムネイル画像が設定されました
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                disabled={isLoading}
                className="bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={isLoading || isUploading}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {isUploading ? 'アップロード中...' : isLoading ? '保存中...' : editingVideo ? '更新' : '作成'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Videos List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-base font-medium text-gray-900">
              動画一覧 ({videos.length}件)
            </h3>
            {videos.length > 0 && (
              <div className="flex items-center space-x-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedVideos.size === videos.length}
                    onChange={selectAllVideos}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">すべて選択</span>
                </label>
                {selectedVideos.size > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {selectedVideos.size}件選択中
                    </span>
                    <button
                      onClick={() => setShowBulkActions(!showBulkActions)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium"
                    >
                      一括操作
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Bulk Actions Panel */}
        {showBulkActions && selectedVideos.size > 0 && (
          <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-900">
                  一括操作 ({selectedVideos.size}件選択中):
                </span>
                <div className="flex items-center space-x-2">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkCategoryUpdate(e.target.value)
                        e.target.value = ''
                      }
                    }}
                    disabled={categoriesLoading || isLoading}
                    className="text-sm border border-blue-300 rounded px-2 py-1 bg-white disabled:bg-gray-100 disabled:opacity-50"
                    defaultValue=""
                  >
                    <option value="">
                      {categoriesLoading ? '読み込み中...' : 'カテゴリーを変更'}
                    </option>
                    {!categoriesLoading && categories.filter(cat => cat.id !== 'all').map(cat => (
                      <option key={cat.id} value={cat.id} title={cat.description}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleBulkDelete}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium"
                  >
                    一括削除
                  </button>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowBulkActions(false)
                  setSelectedVideos(new Set())
                }}
                className="text-blue-600 hover:text-blue-500 text-sm font-medium"
              >
                キャンセル
              </button>
            </div>
          </div>
        )}
        
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">読み込み中...</p>
          </div>
        ) : videos.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {videos.map((video) => (
              <div key={video.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 flex flex-col items-center space-y-2">
                    <input
                      type="checkbox"
                      checked={selectedVideos.has(video.id)}
                      onChange={() => toggleVideoSelection(video.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <VideoThumbnail thumbnailUrl={video.thumbnail_url} title={video.title} className="w-40 h-24 rounded-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-base font-medium text-gray-900 truncate">
                          {video.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {video.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span>カテゴリー: {categories.find(c => c.id === video.category)?.name || video.category}</span>
                          {video.duration && <span>長さ: {video.duration}</span>}
                          <span>視聴回数: {video.view_count}</span>
                          {video.video_type === 'demo' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              デモ動画
                            </span>
                          ) : video.video_type === 'chat_only' ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              チャットのみ
                            </span>
                          ) : (
                            <span>Vimeo ID: {video.vimeo_id}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(videoTags[video.id] || []).map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                              title={tag.description || undefined}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>

                        {/* Attachments summary */}
                        <div className="mt-2">
                          {(videoAttachments[video.id] || []).length > 0 ? (
                            <span className="text-xs text-green-600">
                              📎 添付ファイル: {videoAttachments[video.id].length}件
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">📎 添付ファイルなし</span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEditVideo(video)}
                          className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                        >
                          編集
                        </button>
                        <button
                          onClick={() => handleDeleteVideo(video)}
                          className="text-red-600 hover:text-red-500 text-sm font-medium"
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-500">動画がありません</p>
            <button
              onClick={handleAddVideo}
              className="mt-2 text-blue-600 hover:text-blue-500 text-sm font-medium"
            >
              最初の動画を追加
            </button>
          </div>
        )}
      </div>

      {error && !showAddForm && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  )
}

export default VideoManager